/**
 * Zugriff auf die Encom-API.
 *
 * Vor jeder Anfrage wird das Zugriffstoken erneuert, falls es demnächst
 * abläuft — sonst kippt mitten im Blättern eine Anfrage in ein 401.
 */

import { erneuernFallsNoetig, verwerfen, zugriffstoken } from "@/auth.ts";

export class ApiFehler extends Error {
  constructor(
    public status: number,
    text: string,
  ) {
    super(text);
    this.name = "ApiFehler";
  }
}

async function anfrage<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  await erneuernFallsNoetig();

  const token = zugriffstoken();
  const kopf = new Headers(optionen.headers);
  if (token) kopf.set("Authorization", `Bearer ${token}`);

  const antwort = await fetch(`/api/v1${pfad}`, { ...optionen, headers: kopf });

  if (antwort.status === 401) {
    verwerfen();
    // Sagt dem Rahmen Bescheid, damit er zur Anmeldung führt, statt die Seite
    // mit einem leeren Zustand stehenzulassen.
    window.dispatchEvent(new Event("ribbon:abgemeldet"));
    throw new ApiFehler(401, "Anmeldung abgelaufen");
  }

  if (!antwort.ok) {
    let text = `HTTP ${antwort.status}`;
    try {
      const koerper = await antwort.json();
      if (koerper?.fehler) text = koerper.fehler;
    } catch {
      // Kein JSON im Fehlerfall — dann bleibt es bei der Statuszeile.
    }
    throw new ApiFehler(antwort.status, text);
  }

  return (await antwort.json()) as T;
}

// ------------------------------------------------------------------- Typen

export interface Verlaufseintrag {
  id: number;
  art: "visit" | "activity";
  /** Ortszeit als HH:MM — was die Uhr *dort* zeigte. */
  von: string;
  bis: string;
  /** Dieselben Zeitpunkte roh, für die Anzeige in der Zeitzone des Betrachters. */
  vonUtc: string;
  bisUtc: string;
  offsetMin: number | null;
  beschriftung: string;
  placeId: string | null;
  lat: number | null;
  lon: number | null;
  startLat: number | null;
  startLon: number | null;
  endLat: number | null;
  endLon: number | null;
  distanzM: number | null;
  semantikTyp: string | null;
  abgeleiteteZeit: boolean;
}

export interface Spurpunkt {
  t: string;
  lat: number;
  lon: number;
}

export interface Tag {
  datum: string;
  verlauf: Verlaufseintrag[];
  spur: Spurpunkt[][];
}

export interface OrtTreffer {
  placeId: string;
  name: string | null;
  besuche: number;
  lat: number | null;
  lon: number | null;
  semantikTyp: string | null;
  erstmals: string | null;
  zuletzt: string | null;
}

export interface OrtDetail extends OrtTreffer {
  label: string | null;
  adresse: string | null;
  stadt: string | null;
  land: string | null;
  aufgeloestAm: string | null;
}

export interface Statistik {
  von: string | null;
  bis: string | null;
  segmente: number;
  besuche: number;
  wege: number;
  spuren: number;
  reisen: number;
  punkte: number;
  abgeloest: number;
  importe: number;
  /** Wann zuletzt ein Export eingelesen wurde — null, solange keiner ankam. */
  letzterImportAm: string | null;
  orte: number;
  orteAufgeloest: number;
  orteOffen: number;
  /** Teilmenge von orteOffen: ohne Koordinaten, also nie auflösbar. */
  orteOhneKoordinaten: number;
  jahre: { jahr: string; anzahl: number }[];
  aktivitaeten: { art: string; anzahl: number; meter: number }[];
}

/** Abruf von Geräten und Familie über iCloud.com. */
export interface ICloudStatus {
  zustand: "aus" | "startet" | "bereit" | "code-noetig" | "abgewiesen" | "fehler";
  seitMs: number;
  letzterAbrufMs: number | null;
  geraete: number;
  letzterFehler: string | null;
  intervallMin: number;
  /** Eine Anmeldung wartet auf den Zwei-Faktor-Code. */
  codeErwartet: boolean;
  /** Hinterlegte Nummern, von Apple verdeckt. */
  telefone: { id: number; nummer: string }[];
  /** Wohin der Code per SMS ging — null: an die Geräte. */
  smsAn: number | null;
  /** Apple-ID, gekürzt. */
  appleId: string | null;
  zugangAus: "oberflaeche" | "env" | null;
  /** Zeitachsen-Exporte aus dem iCloud Drive. */
  drive: {
    letzterBlickMs: number | null;
    letzterImport: {
      dateiname: string;
      amMs: number;
      neu: number;
      revidiert: number;
      schonBekannt: boolean;
    } | null;
    fehler: string | null;
  };
}

export interface Importzeile {
  id: number;
  importiertAm: string;
  quelle: string;
  dateiname: string | null;
  bytes: number;
  gesehen: number;
  neu: number;
  revidiert: number;
  bekannt: number;
  von: string | null;
  bis: string | null;
}

export interface IngestBilanz {
  importId: number | null;
  format: string | null;
  schonBekannt: boolean;
  gesehen: number;
  neu: number;
  revidiert: number;
  unveraendert: number;
  punkte: number;
  neueOrte: number;
  von: string | null;
  bis: string | null;
  warnungen: string[];
}

// --------------------------------------------------------------- Zeitraum

interface Segmentzeile {
  id: number;
  art: string;
  vonUtc: string;
  bisUtc: string;
  offsetMin: number | null;
  abgeleiteteZeit: boolean;
  ortsname: string | null;
  aktivitaetLesbar: string | null;
  placeId: string | null;
  lat: number | null;
  lon: number | null;
  startLat: number | null;
  startLon: number | null;
  endLat: number | null;
  endLon: number | null;
  distanzM: number | null;
  semantikTyp: string | null;
}

/**
 * Ein Segment in einen Verlaufseintrag übersetzen.
 *
 * `von`/`bis` tragen die **Ortszeit** — was die Uhr dort zeigte. Angezeigt wird
 * sie nur als Zusatz; die Oberfläche rechnet aus `vonUtc`/`bisUtc` die Zeitzone
 * dessen, der hinsieht (siehe `zeit.ts`).
 */
function segmentZuVerlauf(s: Segmentzeile): Verlaufseintrag {
  const offsetMs = (s.offsetMin ?? 0) * 60_000;
  const vonDate = new Date(Date.parse(s.vonUtc) + offsetMs);
  const bisDate = new Date(Date.parse(s.bisUtc) + offsetMs);

  const dd = (d: Date) => d.toISOString().slice(11, 16);

  return {
    id: s.id,
    art: s.art as "visit" | "activity",
    von: dd(vonDate),
    bis: dd(bisDate),
    vonUtc: s.vonUtc,
    bisUtc: s.bisUtc,
    offsetMin: s.offsetMin,
    beschriftung:
      s.art === "visit"
        ? (s.ortsname ?? "unbekannter Ort")
        : (s.aktivitaetLesbar ?? "Weg"),
    placeId: s.placeId,
    lat: s.lat,
    lon: s.lon,
    startLat: s.startLat,
    startLon: s.startLon,
    endLat: s.endLat,
    endLon: s.endLon,
    distanzM: s.distanzM,
    semantikTyp: s.semantikTyp,
    abgeleiteteZeit: s.abgeleiteteZeit,
  };
}

// ------------------------------------------------------------------ Aufrufe

export const api = {
  statistik: () => anfrage<Statistik>("/stats"),

  icloud: () => anfrage<ICloudStatus>("/icloud"),
  /** Ohne Passwort bleibt das bisherige (nur bei derselben Apple-ID). */
  icloudZugang: (benutzer: string, passwort: string) =>
    anfrage<ICloudStatus>("/icloud/zugang", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(passwort ? { benutzer, passwort } : { benutzer }),
    }),
  /** Beginnt die Anmeldung; der Code kommt danach per SMS (`icloudSms`). */
  icloudAnmelden: () => anfrage<ICloudStatus>("/icloud/anmelden", { method: "POST" }),
  icloudSms: (telefon: number) =>
    anfrage<ICloudStatus>("/icloud/sms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ telefon }),
    }),
  icloudCode: (code: string) =>
    anfrage<ICloudStatus>("/icloud/code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    }),
  importe: () => anfrage<Importzeile[]>("/importe"),

  /**
   * Ein Tag — abgegrenzt so, wie er hier gilt.
   *
   * Die UTC-Grenzen des örtlichen Tages gehen mit, weil nur der Browser die
   * Zeitzone des Betrachters kennt (samt Sommerzeit). Ohne sie fiele der Server
   * auf den UTC-Tag zurück und zeigte in Mitteleuropa je zwei Stunden vom
   * Vor- und Folgetag.
   */
  tag: (datum: string) => {
    const vonMs = new Date(`${datum}T00:00:00`).getTime();
    const bisMs = new Date(`${datum}T23:59:59.999`).getTime();
    return anfrage<Tag>(`/tag/${datum}?vonMs=${vonMs}&bisMs=${bisMs}`);
  },

  kalender: (jahr: number, monat: number) =>
    anfrage<string[]>(`/kalender?jahr=${jahr}&monat=${monat}`),

  orte: (suchwort: string, limit = 60) =>
    anfrage<OrtTreffer[]>(`/orte?q=${encodeURIComponent(suchwort)}&limit=${limit}`),

  ort: (placeId: string) => anfrage<OrtDetail>(`/orte/${encodeURIComponent(placeId)}`),

  ortBenennen: (placeId: string, label: string | null) =>
    anfrage<OrtDetail>(`/orte/${encodeURIComponent(placeId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label }),
    }),

  umkreis: (lat: number, lon: number, radius: number) =>
    anfrage<(Verlaufseintrag & { entfernungM: number; ortsname: string | null })[]>(
      `/umkreis?lat=${lat}&lon=${lon}&radius=${radius}`,
    ),

  segmentKorrigieren: (id: number, korrektur: { placeId?: string | null; lat?: number | null; lon?: number | null; label?: string | null }) =>
    anfrage<Verlaufseintrag>(`/segmente/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(korrektur),
    }),

  /**
   * Verlauf und Spur über einen Zeitraum.
   *
   * Die Grenzen entstehen aus der örtlichen Mitternacht, nicht aus der von
   * Greenwich — sonst begänne ein Sommertag hier um zwei Uhr nachts.
   */
  zeitraum: async (von: string, bis: string): Promise<Tag> => {
    const vonIso = new Date(`${von}T00:00:00`).toISOString();
    const bisIso = new Date(`${bis}T23:59:59.999`).toISOString();
    const [segmente, spurDaten] = await Promise.all([
      anfrage<Segmentzeile[]>(
        `/zeitraum?von=${encodeURIComponent(vonIso)}&bis=${encodeURIComponent(bisIso)}`,
      ),
      anfrage<Spurpunkt[][]>(
        `/spur?von=${encodeURIComponent(vonIso)}&bis=${encodeURIComponent(bisIso)}`,
      ),
    ]);
    return {
      datum: `${von} – ${bis}`,
      verlauf: segmente.map(segmentZuVerlauf),
      spur: spurDaten,
    };
  },

  /**
   * Eine Rückmeldung abgeben. Sie wird ein Issue im Repository des Projekts.
   *
   * Das Bildschirmfoto geht als data:-URI mit — deshalb kann der Rumpf einige
   * Megabyte wiegen.
   */
  rueckmeldung: (daten: {
    art: string;
    nachricht: string;
    seite: string;
    fenster: string;
    browser: string;
    oberflaeche: string;
    screenshot: string | null;
  }) =>
    anfrage<{ nummer: number; url: string }>("/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(daten),
    }),

  /** Einen Export hochladen. Der Rumpf geht als Datei, nicht als JSON. */
  einliefern: (datei: File) => {
    const formular = new FormData();
    formular.append("datei", datei);
    return anfrage<IngestBilanz>("/ingest", { method: "POST", body: formular });
  },

  quellen: () => anfrage<QuelleInfo[]>("/quellen"),

  positionenAktuell: () => anfrage<PositionEintrag[]>("/positionen/aktuell"),

  positionen: (von: string, bis: string, quelle?: string) => {
    const vonIso = new Date(von).toISOString();
    const bisIso = new Date(bis).toISOString();
    const q = quelle ? `&quelle=${encodeURIComponent(quelle)}` : "";
    return anfrage<PositionEintrag[]>(
      `/positionen?von=${encodeURIComponent(vonIso)}&bis=${encodeURIComponent(bisIso)}${q}`,
    );
  },

  fluege: (lamin: number, lomin: number, lamax: number, lomax: number) =>
    anfrage<FlugAntwort>(`/overlay/fluege?lamin=${lamin}&lomin=${lomin}&lamax=${lamax}&lomax=${lomax}`),

  wetter: (lat: number, lon: number) => anfrage<Wetter>(`/overlay/wetter?lat=${lat}&lon=${lon}`),

  flugDetail: (hex: string, rufzeichen?: string | null) =>
    anfrage<FlugDetail>(`/overlay/flug/${encodeURIComponent(hex)}${rufzeichen ? `?rufzeichen=${encodeURIComponent(rufzeichen)}` : ""}`),

  blitze: () => anfrage<BlitzAntwort>("/overlay/blitze"),

  erdbeben: () => anfrage<{ beben: Erdbeben[] }>("/overlay/erdbeben"),

  wind: (lat: number, lon: number) => anfrage<Wind>(`/overlay/wind?lat=${lat}&lon=${lon}`),

  iss: () => anfrage<IssPosition>("/overlay/iss"),

  ladestationen: (lamin: number, lomin: number, lamax: number, lomax: number) =>
    anfrage<{ stationen: Ladestation[] }>(`/overlay/ladestationen?lamin=${lamin}&lomin=${lomin}&lamax=${lamax}&lomax=${lomax}`),

  ladepreise: (lat: number, lon: number) => anfrage<LadepreisAntwort>(`/overlay/ladepreise?lat=${lat}&lon=${lon}`),

  schiffe: (lamin: number, lomin: number, lamax: number, lomax: number) =>
    anfrage<{ schiffe: Schiff[] }>(`/overlay/schiffe?lamin=${lamin}&lomin=${lomin}&lamax=${lamax}&lomax=${lomax}`),

  feuer: (lamin: number, lomin: number, lamax: number, lomax: number) =>
    anfrage<{ feuer: Feuer[] }>(`/overlay/feuer?lamin=${lamin}&lomin=${lomin}&lamax=${lamax}&lomax=${lomax}`),

  personenUebersicht: () => anfrage<PersonenUebersicht>("/personen"),

  personAnlegen: (name: string, farbe?: string) =>
    anfrage<Person>("/personen", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, farbe }),
    }),

  personAktualisieren: (id: number, aenderung: { name?: string; farbe?: string }) =>
    anfrage<Person>(`/personen/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(aenderung),
    }),

  personLoeschen: (id: number) =>
    anfrage<void>(`/personen/${id}`, { method: "DELETE" }),

  quelleZuordnen: (quelleId: string, personId: number | null) =>
    anfrage<void>(`/quellen/${encodeURIComponent(quelleId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ personId }),
    }),
};

export interface Feuer {
  lat: number;
  lon: number;
  frp: number | null;
  konfidenz: number | null;
  zeitMs: number;
  quelle: string;
}

export interface Schiff {
  mmsi: number;
  name: string | null;
  lat: number;
  lon: number;
  kurs: number | null;
  tempo: number | null;
  typ: number | null;
  zeitMs: number;
}

export interface Ladestation {
  id: string;
  name: string;
  betreiber: string | null;
  lat: number;
  lon: number;
  kw: number | null;
  ladepunkte: number | null;
  adresse: string | null;
}

export interface Ladetarif {
  anbieter: string | null;
  tarif: string | null;
  preis: number;
  proKwh: number | null;
  waehrung: string;
  monatsgebuehr: number | null;
}

export interface LadepreisAntwort {
  station: string | null;
  ladepunkt: { kw: number | null; stecker: string | null };
  tarife: Ladetarif[];
}

export interface Erdbeben {
  id: string;
  magnitude: number | null;
  ort: string | null;
  lat: number;
  lon: number;
  tiefe: number | null;
  zeitMs: number;
  url: string | null;
}

export interface Wind {
  geschwindigkeit: number | null;
  richtung: number | null;
  boeen: number | null;
}

export interface IssPosition {
  lat: number;
  lon: number;
  hoehe: number | null;
  geschwindigkeit: number | null;
}

export interface Flug {
  id: string;
  rufzeichen: string | null;
  land: string | null;
  lat: number;
  lon: number;
  hoehe: number | null;
  kurs: number | null;
  geschwindigkeit: number | null;
  steigen: number | null;
  amBoden: boolean;
}

export interface FlugAntwort {
  zeit: number;
  fluege: Flug[];
}

export interface Flughafen {
  name: string | null;
  stadt: string | null;
  iata: string | null;
  land: string | null;
}

export interface FlugDetail {
  rufzeichen: string;
  airline: string | null;
  airlineLand: string | null;
  von: Flughafen | null;
  nach: Flughafen | null;
  flugzeug: string | null;
  hersteller: string | null;
  registrierung: string | null;
}

export interface Blitz {
  id: string;
  lat: number;
  lon: number;
  zeitMs: number;
  entfernungKm: number | null;
}

export interface BlitzAntwort {
  blitze: Blitz[];
}

export interface Wetter {
  temperatur: number | null;
  zustand: string | null;
  windGeschwindigkeit: number | null;
  bewoelkung: number | null;
  station: string | null;
  stationEntfernung: number | null;
}

// -------------------------------------------------------- Positionsquellen

export interface QuelleInfo {
  id: string;
  art: string;
  name: string;
  farbe: string;
  symbol: string | null;
  aktiv: boolean;
  zuletztMs: number | null;
  anzahl: number;
  ersteMs: number | null;
}

export interface PositionEintrag {
  id: number;
  quelleId: string;
  quelleName: string;
  quelleArt: string;
  farbe: string;
  zeitpunkt: string;
  tMs: number;
  lat: number;
  lon: number;
  genauigkeit: number | null;
  hoehe: number | null;
  richtung: number | null;
  meta: Record<string, unknown> | null;
}

/**
 * Die Positionen einer Quelle über einen Zeitraum, zu einer Spur gebündelt.
 * Die Zeitachse zeichnet je Spur eine Linie mit Punkten in der Quellfarbe.
 */
export interface FremdSpur {
  quelleId: string;
  name: string;
  art: string;
  farbe: string;
  punkte: PositionEintrag[];
}

/** Positionen eines Zeitraums nach Quelle bündeln — leere Quellen entfallen. */
export function zuFremdspuren(positionen: PositionEintrag[]): FremdSpur[] {
  const nach = new Map<string, FremdSpur>();
  for (const p of positionen) {
    let spur = nach.get(p.quelleId);
    if (!spur) {
      spur = { quelleId: p.quelleId, name: p.quelleName, art: p.quelleArt, farbe: p.farbe, punkte: [] };
      nach.set(p.quelleId, spur);
    }
    spur.punkte.push(p);
  }
  for (const spur of nach.values()) spur.punkte.sort((a, b) => a.tMs - b.tMs);
  return [...nach.values()];
}

// --------------------------------------------------------- Personen

export type GeraetStatus = "bewegt" | "steht" | "veraltet";

export interface GeraetMitStatus {
  id: string;
  name: string;
  art: string;
  farbe: string;
  aktiv: boolean;
  personId: number | null;
  status: GeraetStatus;
  bewegungM: number;
  lat: number | null;
  lon: number | null;
  zuletztMs: number | null;
}

export interface Person {
  id: number;
  name: string;
  farbe: string;
  symbol: string | null;
  aktiv: boolean;
}

export interface PersonMitGeraeten extends Person {
  geraete: GeraetMitStatus[];
}

export interface PersonenUebersicht {
  personen: PersonMitGeraeten[];
  ohnePerson: GeraetMitStatus[];
}
